import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCustomer } from "@/lib/customerAuth";
import { LogOut, RefreshCw, Wrench, User } from "lucide-react";

export default function MyBookings() {
  const { user, loading, signIn, signOut } = useCustomer();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { setFetching(false); return; }
    api.get("/customer/bookings").then(r => setBookings(r.data)).finally(() => setFetching(false));
  }, [user, loading]);

  const rebook = (b) => {
    const q = new URLSearchParams({
      brand: b.forklift_brand || "", model: b.forklift_model || "", fuel: b.fuel_type || "",
      machine: b.machine_type || "", capacity: b.capacity || "", service: b.service_type || "",
      problem: b.problem_description || "",
    });
    nav(`/book-service?${q.toString()}`);
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-sm uppercase tracking-widest text-[#666]">Loading…</div>;

  if (!user) return (
    <div className="max-w-md mx-auto px-6 py-20 text-center">
      <User className="w-12 h-12 text-[#D71920] mx-auto" />
      <h1 className="font-display text-4xl uppercase mt-4">Sign in to view your bookings</h1>
      <p className="mt-4 text-[#555] text-sm">Access your booking history and re-book with a single click.</p>
      <div className="mt-8 flex flex-col gap-3">
        <Link to="/login" className="ff-btn-primary" data-testid="my-signin-email">Sign in with Email</Link>
        <button onClick={signIn} className="w-full border-2 border-[#111] px-6 py-3 text-sm uppercase tracking-widest font-bold hover:bg-[#111] hover:text-white transition" data-testid="my-signin">Sign in with Google</button>
        <p className="text-sm text-[#555] mt-2">
          New to Forkfix? <Link to="/register" className="text-[#D71920] font-bold hover:underline" data-testid="my-register">Create account</Link>
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex justify-between items-start flex-wrap gap-3 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">My account</div>
          <h1 className="font-display text-4xl uppercase mt-2">My bookings</h1>
          <p className="text-sm text-[#555] mt-1">{user.email}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/book-service" className="ff-btn-primary"><Wrench className="w-4 h-4" /> New Booking</Link>
          <button onClick={signOut} className="ff-btn-ghost" data-testid="my-signout"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </div>
      {(() => {
        if (fetching) return <div className="text-center py-16 text-sm uppercase tracking-widest text-[#666]">Loading…</div>;
        if (bookings.length === 0) return (
          <div className="ff-card p-10 text-center">
            <h3 className="font-display text-2xl uppercase">No bookings yet</h3>
            <p className="text-sm text-[#555] mt-3">Submit your first service request and it'll show up here.</p>
            <Link to="/book-service" className="ff-btn-primary mt-5 inline-flex">Book a Service</Link>
          </div>
        );
        return (
        <div className="overflow-x-auto ff-card">
          <table className="w-full text-sm">
            <thead className="bg-[#111] text-white text-[11px] uppercase tracking-widest">
              <tr><th className="p-3 text-left">Ref</th><th className="p-3 text-left">Date</th><th className="p-3 text-left">Forklift</th><th className="p-3 text-left">Service</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b.booking_id} className="border-t border-[#e5e5e5]" data-testid={`my-b-${b.booking_reference}`}>
                  <td className="p-3 font-mono-tech font-bold">{b.booking_reference}</td>
                  <td className="p-3">{b.preferred_date}</td>
                  <td className="p-3">{b.forklift_brand} {b.forklift_model}</td>
                  <td className="p-3 text-xs">{b.service_type}</td>
                  <td className="p-3"><span className={`text-[11px] uppercase font-bold ${b.status === "Confirmed" ? "text-[#D71920]" : ""}`}>{b.status}</span></td>
                  <td className="p-3 text-right"><button onClick={() => rebook(b)} className="text-xs uppercase font-bold tracking-widest hover:text-[#D71920] inline-flex items-center gap-1" data-testid={`rebook-${b.booking_reference}`}><RefreshCw className="w-3 h-3" /> Re-book</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}
    </div>
  );
}
